<template>
	<b-form class="new-event-local-tour-form" @submit.prevent="submit">
		<button
			type="button"
			class="new-event-local-tour-form__toggle"
			:aria-label="translation.title"
		></button>

		<h3>{{translation.title}}</h3>

		<div class="new-event-local-tour-form__grid">
			<div class="new-event-local-tour-form__field new-event-local-tour-form__field--full">
				<b-form-input
					v-model="booking.pickupPlace"
					class="new-event-local-tour-form__control"
					type="text"
					:placeholder="translation.hotel"
					required
				/>
			</div>

			<div class="new-event-local-tour-form__field">
				<b-form-input
					v-model="pax.name"
					class="new-event-local-tour-form__control"
					type="text"
					:placeholder="translation.name"
					required
				/>
			</div>

			<div class="new-event-local-tour-form__field">
				<b-form-input
					v-model="pax.email"
					class="new-event-local-tour-form__control"
					type="email"
					:placeholder="translation.email"
					required
				/>
			</div>

			<div class="new-event-local-tour-form__field">
				<date-picker
					v-model="booking.date"
					class="new-event-local-tour-form__date"
					:initialDefaultDate="initialDefaultDate"
					:placeholder="translation.date"
					required
				/>
			</div>

			<div class="new-event-local-tour-form__field new-event-local-tour-form__field--select">
				<b-form-select
					v-model="selectedCountryId"
					class="new-event-local-tour-form__control new-event-local-tour-form__select"
					:options="countryOptions"
					required
				/>
			</div>

			<div class="new-event-local-tour-form__field new-event-local-tour-form__field--passengers">
				<label class="new-event-local-tour-form__label" :for="passengerFieldId">
					{{translation.qtdPassenger}}
				</label>
				<div class="new-event-local-tour-form__select-wrap">
					<b-form-select
						:id="passengerFieldId"
						v-model="booking.adultsQty"
						class="new-event-local-tour-form__control new-event-local-tour-form__select"
						:options="passengerOptions"
						required
					/>
				</div>
			</div>

			<div class="new-event-local-tour-form__field new-event-local-tour-form__field--textarea">
				<b-form-textarea
					v-model="booking.observations"
					class="new-event-local-tour-form__control new-event-local-tour-form__textarea"
					:placeholder="translation.message"
				/>
			</div>
		</div>

		<div class="new-event-local-tour-form__passenger-row">
			<div class="new-event-local-tour-form__passengers">
				<fieldset
					v-for="(guest, guestIndex) in booking.guests"
					:key="guestIndex"
					class="new-event-local-tour-form__guest"
				>
					<legend>{{translation.namePassenger}} {{guestIndex + 1}}</legend>
					<b-form-input
						v-model="guest.name"
						class="new-event-local-tour-form__control"
						type="text"
						:placeholder="translation.name"
						required
					/>
				</fieldset>
			</div>

			<button type="submit" class="new-event-local-tour-form__button">
				{{lowercaseLabel(translation.button)}}
			</button>
		</div>
	</b-form>
</template>

<script>
import moment from 'moment';
import { translate } from '../../assets/translations';
import DatePicker from '../shared/DatePicker.vue';

export default {
	components: {
		DatePicker,
	},
	props: {
		countries: {
			type: Array,
			default: () => [],
		},
		event: {
			type: Object,
			required: true,
		},
		tour: {
			type: Object,
			required: true,
		},
	},
	data() {
		return {
			booking: {
				adultsQty: 1,
				date: this.initialDateValue(),
				guests: [{ name: null }],
				observations: null,
				pickupPlace: null,
			},
			pax: {
				email: null,
				name: null,
			},
			selectedCountryId: null,
		};
	},
	computed: {
		countryOptions() {
			const placeholder = {
				value: null,
				text: this.lowercaseLabel(this.translation.country),
				disabled: true,
			};
			return [placeholder].concat(this.countries.map(country => ({
				value: country.id,
				text: country.name,
			})));
		},
		initialDefaultDate() {
			if (this.event && this.event.startDate) {
				return moment(this.event.startDate, 'YYYY-MM-DD').format('DD/MM/YYYY');
			}
			return moment().format('DD/MM/YYYY');
		},
		passengerOptions() {
			return this.buildArrayOfNumbers(10);
		},
		passengerFieldId() {
			return `new-local-tour-passengers-${this.tour.id}`;
		},
		translation() {
			return translate(this.$store.state.language).localTour.form;
		},
	},
	watch: {
		'booking.adultsQty': function syncGuestList() {
			const currentGuests = this.booking.guests;
			const guests = [];
			const adultsQty = Number(this.booking.adultsQty) || 1;
			for (let index = 0; index < adultsQty; index++) {
				guests.push(currentGuests[index] || { name: null });
			}
			this.booking.guests = guests;
		},
	},
	methods: {
		buildArrayOfNumbers(number) {
			let numberArray = [];
			for (let index = 1; index <= number; index++) {
				numberArray.push(index);
			}
			return numberArray;
		},
		countryForId(countryId) {
			return this.countries.find(country => country.id === countryId) || null;
		},
		initialDateValue() {
			if (this.event && this.event.startDate) {
				return moment(this.event.startDate, 'YYYY-MM-DD').toISOString();
			}
			return moment().toISOString();
		},
		lowercaseLabel(label) {
			return String(label || '').toLowerCase();
		},
		submit() {
			const pax = Object.assign({}, this.pax, {
				country: this.countryForId(this.selectedCountryId),
			});
			const booking = Object.assign({}, this.booking, {
				adultsQty: Number(this.booking.adultsQty) || 1,
				guests: this.booking.guests.map(guest => Object.assign({}, guest)),
			});

			this.$emit('reserve', {
				tour: this.tour,
				pax,
				booking,
			});
		},
	},
};
</script>

<style scoped lang="scss">
.new-event-local-tour-form {
	box-sizing: border-box;
	margin: 52px -53px -58px;
	padding: 42px 68px 58px;
	border-top: 1px solid #d7d7d7;
	border-radius: 0 0 30px 30px;
	background: #eeeeee;
	color: #002c41;
}

.new-event-local-tour-form__toggle {
	display: block;
	width: 23px;
	height: 12px;
	margin: 0 0 22px;
	padding: 0;
	border: 0;
	background: url("data:image/svg+xml,%3Csvg width='23' height='12' viewBox='0 0 23 12' fill='none' xmlns='http://www.w3.org/2000/svg'%3E%3Cpath d='M2 2L11.5 10L21 2' stroke='%2366ACC6' stroke-width='3'/%3E%3C/svg%3E") center / contain no-repeat;
	cursor: default;
}

.new-event-local-tour-form h3 {
	margin: 0 0 42px;
	color: #002c41;
	font-size: 18px;
	font-weight: 700;
	line-height: 21px;
}

.new-event-local-tour-form__grid {
	display: flex;
	flex-wrap: wrap;
	gap: 12px 11px;
	max-width: 959px;
}

.new-event-local-tour-form__field {
	position: relative;
	flex: 1 1 calc(50% - 6px);
	min-width: 0;
}

.new-event-local-tour-form__field--full,
.new-event-local-tour-form__field--textarea {
	flex-basis: 100%;
}

.new-event-local-tour-form__field--passengers {
	display: flex;
	flex: 0 0 200px;
	flex-direction: column;
	width: 200px;
}

.new-event-local-tour-form__label {
	margin: 0 0 8px;
	color: #002c41;
	font-size: 16px;
	font-weight: 500;
	line-height: 19px;
}

.new-event-local-tour-form__select-wrap {
	position: relative;
	width: 100%;
}

.new-event-local-tour-form__field--select::after,
.new-event-local-tour-form__select-wrap::after {
	content: "";
	position: absolute;
	top: 22px;
	right: 19px;
	width: 13px;
	height: 13px;
	border-right: 3px solid #66acc6;
	border-bottom: 3px solid #66acc6;
	pointer-events: none;
	transform: rotate(45deg) translateY(-4px);
}

.new-event-local-tour-form__control,
.new-event-local-tour-form /deep/ .form-control,
.new-event-local-tour-form__select,
.new-event-local-tour-form__date /deep/ .datetime-input {
	display: block;
	box-sizing: border-box;
	width: 100%;
	height: 58px !important;
	border: 1px solid rgba(0, 0, 0, .2);
	border-radius: 10px;
	background-color: #ffffff;
	color: #5a5a5a;
	font-family: inherit;
	font-size: 18px;
	font-weight: 400;
	line-height: 18px;
	outline: none;
	box-shadow: none;
	transition: border-color .15s ease-in-out, box-shadow .15s ease-in-out;
}

.new-event-local-tour-form__date /deep/ .datetime-input {
	padding-left: 12px;
}

.new-event-local-tour-form__select {
	padding-right: 54px;
	background-image: none;
	appearance: none;
}

.new-event-local-tour-form__control:focus,
.new-event-local-tour-form /deep/ .form-control:focus,
.new-event-local-tour-form__select:focus,
.new-event-local-tour-form__date /deep/ .datetime-input:focus {
	border-color: #66acc6;
	box-shadow: 0 0 0 .2rem rgba(102, 172, 198, .15);
}

.new-event-local-tour-form__control::placeholder,
.new-event-local-tour-form /deep/ .form-control::placeholder,
.new-event-local-tour-form__date /deep/ .datetime-input::placeholder {
	color: #5a5a5a;
	opacity: 1;
}

.new-event-local-tour-form__date /deep/ .calendar-icon {
	width: 33px;
	right: 15px;
	bottom: 11px;
}

.new-event-local-tour-form__textarea {
	height: 137px;
	min-height: 137px;
	padding-top: 16px;
	resize: none;
}

.new-event-local-tour-form__passenger-row {
	display: flex;
	align-items: flex-end;
	justify-content: space-between;
	gap: 40px;
	margin-top: 49px;
}

.new-event-local-tour-form__passengers {
	display: flex;
	flex-direction: column;
	gap: 30px;
	width: 100%;
	max-width: 949px;
}

.new-event-local-tour-form__guest {
	min-width: 0;
	margin: 0;
	padding: 0;
	border: 0;
}

.new-event-local-tour-form__guest legend {
	margin: 0 0 20px;
	color: #002c41;
	font-size: 18px;
	font-weight: 700;
	line-height: 21px;
}

.new-event-local-tour-form__button {
	box-sizing: border-box;
	flex: 0 0 auto;
	width: 154px;
	height: 34px;
	margin-bottom: 8px;
	border: 1px solid #ff9600;
	border-radius: 10px;
	background: #ff9600;
	color: #ffffff;
	font-family: inherit;
	font-size: 16px;
	font-weight: 700;
	line-height: 19px;
	text-align: center;
	box-shadow: 4px 4px 100px rgba(0, 0, 0, .1);
	cursor: pointer;
}

@media (max-width: 900px) {
	.new-event-local-tour-form {
		margin: 36px -24px -24px;
		padding: 28px 24px 36px;
		border-radius: 0 0 24px 24px;
	}

	.new-event-local-tour-form h3 {
		margin-bottom: 28px;
	}

	.new-event-local-tour-form__grid {
		max-width: none;
	}

	.new-event-local-tour-form__field {
		flex-basis: 100%;
	}

	.new-event-local-tour-form__field--full,
	.new-event-local-tour-form__field--textarea {
		flex-basis: 100%;
	}

	.new-event-local-tour-form__field--passengers {
		flex-basis: 100%;
		width: 100%;
	}

	.new-event-local-tour-form__passenger-row {
		flex-direction: column;
		align-items: flex-start;
		gap: 24px;
		margin-top: 34px;
	}

	.new-event-local-tour-form__button {
		width: 100%;
		max-width: 154px;
		margin-bottom: 0;
	}
}
</style>



// WEBPACK FOOTER //
// src/components/local-tour/LocalTourReservationForm.vue